/**
 * Per-surface profile-field visibility registry
 * ============================================================================
 *
 * Single source of truth for "which dancer profile fields appear on which
 * surface, for which brand, for which viewer." Render sites consume
 * `isFieldVisible(field, ctx)` instead of hardcoding their own filter rules.
 *
 * Why this exists
 * ---------------
 * A dancer's profile data is the same across surfaces, but the *display*
 * differs along three axes:
 *
 *  1. **Surface** (where is this rendered?) — public dancer profile,
 *     competition snapshot, agency profile, audition snapshot, PDF booklet.
 *  2. **Brand** (which product is the viewer using?) — Flow (career-dancer
 *     site at `flow.ballee.co`) vs Ballee (broader community + agency at
 *     `ballee.co`). The same field can be public on Flow and recruiter-only
 *     on Ballee, or vice versa.
 *  3. **Viewer role** (who is looking?) — anonymous, authenticated peer,
 *     recruiter (any member of `client_members` or `organization_staff`),
 *     profile owner, super admin. Sensitive fields (contact info, weight,
 *     measurements) can be hidden from the general public but exposed to the
 *     recruiter tier, which needs them for casting decisions.
 *
 *     "Recruiter" is a tier, not a seat. It is any client casting-team member or
 *     organization member — there is no separate recruiter role to hold.
 *
 * Without this registry, render sites copy-paste their own conditionals
 * (e.g. "show eye color iff variant === 'public'"), drift over time, and
 * make per-brand / per-audience rules impossible to ship cleanly.
 *
 * Backward compatibility
 * ----------------------
 * The legacy helpers `isFieldVisibleInView(field, view)` and
 * `getFieldsForView(view)` continue to work — they delegate to the new
 * predicate API with a default context (`brand: 'flow', viewer: 'anonymous'`).
 * Existing call sites do not need to change in lockstep with this file;
 * migrate them to `isFieldVisible(field, ctx)` opportunistically when you
 * touch them, or as part of a dedicated wiring phase.
 *
 * How to add a surface
 * --------------------
 *  1. Add the surface to `ViewKey` below.
 *  2. Add an entry to `FIELD_VISIBILITY` listing the fields/media the
 *     surface displays and the rule that gates each.
 *  3. Add tests in `view-fields.test.ts` covering the matrix cells you care
 *     about for that surface.
 *
 * How to add a field
 * ------------------
 *  1. Add the key to `ProfileFieldKey`. Match the `PROFILE_FIELDS`
 *     palette in
 *     `packages/features/auditions/src/components/application-form/application-form-builder.tsx`
 *     when the field is also audition-bindable; the two registries should
 *     stay aligned for fields that exist in both.
 *  2. Add it to each surface in `FIELD_VISIBILITY` that should display it,
 *     pairing it with the right rule (`ALWAYS`, `OWNER_OR_RECRUITER`, etc.).
 *  3. Add a test for the field's visibility per surface / brand / viewer.
 *
 * Per-dancer privacy toggles (shipped 2026-05-13)
 * -----------------------------------------------
 * The dancer can further restrict what's shown publicly via five booleans
 * on `professional_profiles`:
 *   bio_public, measurements_public, contact_public, social_public,
 *   resume_public.
 *
 * AND-only semantics: the dancer's setting AND-gates against the
 * registry's role-based ceiling. It can further restrict but never
 * override. `measurements_public = TRUE` on a public Ballee profile
 * does NOT expose weight to anon viewers — the registry keeps the body
 * measurement fields recruiter-only.
 *
 * Callers pass these as the optional `dancerPreferences` arg on
 * `isFieldVisible` / `isMediaVisible`. Admin viewers bypass the
 * preferences for moderation; everyone else (including the owner viewing
 * their own public preview) is subject to them.
 *
 * Out of scope (future work)
 * --------------------------
 *  - Per-org overrides for view rules (some agencies might want height
 *    visible, others not). Would extend `ViewContext` with `orgId?`.
 *  - Localized field labels. Today the registry only decides visibility;
 *    labels stay in i18n.
 */

import {
  GATED_FIELD_KEYS,
  MEDIA_PREFERENCE_KEY,
  resolvePreference,
} from './view-fields.gating';
import {
  ALL_PUBLIC_MEDIA,
  ALWAYS,
  APPEARANCE_FIELDS,
  AVAILABILITY_FIELDS,
  CAST_SIGNAL_FIELDS,
  DEFAULT_CTX,
  DIMENSION_FIELDS,
  FEVER_OR_ADMIN,
  IDENTITY_FIELDS,
  LOCATION_FIELDS,
  OWNER_OR_RECRUITER,
  PROFESSIONAL_FIELDS,
  SOCIAL_FIELDS,
  withRule,
} from './view-fields.rules';
import type {
  DancerVisibilityPreferences,
  FieldVisibilityRule,
  MediaTypeKey,
  ProfileFieldKey,
  ViewContext,
  ViewKey,
} from './view-fields.types';

export type {
  Brand,
  DancerVisibilityPreferences,
  FieldRule,
  FieldVisibilityRule,
  MediaTypeKey,
  ProfileFieldKey,
  ViewContext,
  ViewerRole,
  ViewKey,
} from './view-fields.types';
export type { GatedColumn, GatedColumnTable } from './view-fields.gating';
export {
  GATED_COLUMNS,
  GATED_COLUMNS_BY_TOGGLE,
  GATED_FIELD_KEYS,
  toggleForColumn,
} from './view-fields.gating';
export {
  ALWAYS,
  anyOf,
  AUTHENTICATED_ONLY,
  CAST_SIGNAL_FIELDS,
  FEVER_OR_ADMIN,
  NEVER,
  onlyOnBrand,
  ONLY_OWNER,
  OWNER_OR_RECRUITER,
} from './view-fields.rules';

// ==================== Visibility map ====================

export const FIELD_VISIBILITY: Readonly<Record<ViewKey, FieldVisibilityRule>> =
  {
    /**
     * Public dancer profile (`/artists/[slug]`).
     *
     * Audience tightening (Phase 5):
     *  - Body measurements (weight + bust/waist/hips) — sensitive
     *    physical data — are visible to OWNER + ADMIN + the RECRUITER tier
     *    only. Anonymous and authenticated peers see height + appearance,
     *    not the full measurement sheet.
     *  - Contact info (email + phone) is OWNER_OR_RECRUITER. Anyone on a
     *    client casting team or in an organization needs to reach the
     *    dancer; the general public should not be scraping personal
     *    contact details.
     *  - Everything else (identity, professional details, dance styles,
     *    height, eye/hair/skin, social handles, availability) stays
     *    publicly visible — these are credentials the dancer wants the
     *    world to see.
     *
     * Brand divergence (Flow vs Ballee) is not yet rule-coded — both
     * brands currently apply the same matrix on this surface. Per-brand
     * predicates can be added field-by-field as business rules emerge
     * (e.g. Ballee may surface contact info to verified agencies).
     */
    public_dancer_profile: {
      fields: {
        ...withRule(IDENTITY_FIELDS, ALWAYS),
        ...withRule(LOCATION_FIELDS, ALWAYS),
        ...withRule(PROFESSIONAL_FIELDS, ALWAYS),
        // Height stays public; weight + body measurements tighten.
        heightCm: ALWAYS,
        weightKg: OWNER_OR_RECRUITER,
        bustCm: OWNER_OR_RECRUITER,
        waistCm: OWNER_OR_RECRUITER,
        hipsCm: OWNER_OR_RECRUITER,
        // Sizes stay public — useful for casting calls without being
        // the kind of body data dancers usually keep private.
        clothingSize: ALWAYS,
        shoeSize: ALWAYS,
        pointeShoeBrand: ALWAYS,
        pointeShoeSize: ALWAYS,
        // Appearance — opt-in via the wizard, public for everyone who
        // sees the profile.
        ...withRule(APPEARANCE_FIELDS, ALWAYS),
        // Contact info — recruiter tier / owner / admin only.
        contactEmail: OWNER_OR_RECRUITER,
        contactPhone: OWNER_OR_RECRUITER,
        // Public credentials.
        ...withRule(SOCIAL_FIELDS, ALWAYS),
        ...withRule(AVAILABILITY_FIELDS, ALWAYS),
        // Meteor-parity sections. Always-on at the registry level; the
        // page hides them when their underlying data is empty.
        featuredEvents: ALWAYS,
        resumeWidget: ALWAYS,
      },
      media: withRule([...ALL_PUBLIC_MEDIA, 'resume' as const], ALWAYS),
    },

    /**
     * Agency dancer profile — forthcoming surface. Until the agency
     * module is scoped, mirror the public profile so the helpers have a
     * stable answer. Agencies typically need contact info that the
     * public profile hides; expose `contactPhone` here as a placeholder.
     */
    agency_dancer_profile: {
      fields: {
        ...withRule(IDENTITY_FIELDS, ALWAYS),
        ...withRule(LOCATION_FIELDS, ALWAYS),
        ...withRule(PROFESSIONAL_FIELDS, ALWAYS),
        ...withRule(DIMENSION_FIELDS, ALWAYS),
        ...withRule(APPEARANCE_FIELDS, ALWAYS),
        ...withRule(SOCIAL_FIELDS, ALWAYS),
        ...withRule(AVAILABILITY_FIELDS, ALWAYS),
        contactPhone: ALWAYS,
        featuredEvents: ALWAYS,
        resumeWidget: ALWAYS,
      },
      media: withRule([...ALL_PUBLIC_MEDIA, 'resume' as const], ALWAYS),
    },

    /**
     * Competition snapshot — locked rules per WIP_closing_audition_field_gaps:
     * height YES, measurements NO, eye/hair/skin NO. Industry-standard
     * for dance competition booklets.
     */
    competition_snapshot: {
      fields: {
        firstName: ALWAYS,
        lastName: ALWAYS,
        profilePhotoUrl: ALWAYS,
        gender: ALWAYS,
        birthdate: ALWAYS,
        nationalities: ALWAYS,
        languages: ALWAYS,
        city: ALWAYS,
        countryOfResidence: ALWAYS,
        bio: ALWAYS,
        yearsOfExperience: ALWAYS,
        danceStyles: ALWAYS,
        danceLevel: ALWAYS,
        heightCm: ALWAYS, // explicit YES — industry standard
      },
      media: {
        headshot: ALWAYS,
        videos: ALWAYS,
      },
    },

    /**
     * Audition snapshot — driven by per-audition `application_structure`.
     * The registry doesn't enumerate; included so callers can detect
     * intent and route to the form-driven path.
     */
    audition_snapshot: {
      fields: {},
      media: {},
    },

    /**
     * PDF booklet template currently mirrors public-profile rules.
     * Diverging from public-profile is a deliberate future decision
     * when the booklet's design needs change.
     */
    dancer_booklet_pdf: {
      fields: {
        ...withRule(IDENTITY_FIELDS, ALWAYS),
        ...withRule(LOCATION_FIELDS, ALWAYS),
        ...withRule(PROFESSIONAL_FIELDS, ALWAYS),
        ...withRule(DIMENSION_FIELDS, ALWAYS),
        ...withRule(APPEARANCE_FIELDS, ALWAYS),
        ...withRule(SOCIAL_FIELDS, ALWAYS),
        ...withRule(AVAILABILITY_FIELDS, ALWAYS),
      },
      media: withRule([...ALL_PUBLIC_MEDIA, 'resume' as const], ALWAYS),
    },

    /**
     * Fever recruitment directory (`/home/(user)/fever-directory`, BAL-316).
     *
     * Card fields (Q3): name + photo (identity), city + country (location),
     * languages, dance level, availability — all visible to fever_staff.
     * The cast-signal fields (total Fever shows, last cast date, 30-day
     * availability, distance, completeness) are FEVER_OR_ADMIN: a fever_staff
     * or super-admin viewer sees them; a dancer peer / owner / anonymous
     * viewer NEVER does. profile_category is deliberately absent from this
     * surface (Q5 — never shown at Fever).
     *
     * Note: absence of a field key here means "not on this surface." The
     * Discover tab renders the same card MINUS the cast signals by computing
     * the field set under the caller's scope (dancer_peer/public strip them).
     */
    fever_directory: {
      fields: {
        // Identity subset — no birthdate/gender/nationalities on the card.
        firstName: ALWAYS,
        lastName: ALWAYS,
        profilePhotoUrl: ALWAYS,
        languages: ALWAYS,
        // Location.
        ...withRule(LOCATION_FIELDS, ALWAYS),
        // Professional signal — skill level yes, NOT profile_category (Q5).
        danceStyles: ALWAYS,
        danceLevel: ALWAYS,
        availableForHire: ALWAYS,
        ...withRule(AVAILABILITY_FIELDS, ALWAYS),
        // Cast signals — Fever staff / super admin only (R4).
        ...withRule(CAST_SIGNAL_FIELDS, FEVER_OR_ADMIN),
      },
      media: {
        headshot: ALWAYS,
      },
    },
  };

// ==================== Helpers ====================

/**
 * Predicate: is this field visible on the given surface, brand, and
 * viewer role? This is the canonical visibility check — prefer it over
 * `isFieldVisibleInView` in new code.
 *
 *  - For `audition_snapshot`, returns `false` for every field because
 *    audition rendering is driven by `application_structure`, not the
 *    view registry. Callers needing audition-specific visibility should
 *    consult the audition's application form configuration directly.
 */
export function isFieldVisible(
  field: ProfileFieldKey,
  ctx: ViewContext,
  dancerPreferences?: DancerVisibilityPreferences,
): boolean {
  const rule = FIELD_VISIBILITY[ctx.surface].fields[field];
  if (!rule || !rule(ctx)) return false;
  // When preferences are omitted entirely, the caller is opting out of
  // the per-dancer AND-gate — registry decides alone (back-compat with
  // every call site that predates the 2026-05-13 toggles).
  if (dancerPreferences === undefined) return true;
  // Admin viewers bypass per-dancer toggles for moderation.
  if (ctx.viewer === 'admin') return true;
  const prefKey = GATED_FIELD_KEYS[field];
  if (!prefKey) return true;
  return resolvePreference(dancerPreferences, prefKey);
}

/**
 * Predicate: is this media type visible on the given surface, brand, and
 * viewer role? AND-gated with the dancer's per-section toggle when
 * `dancerPreferences` is supplied (see `DancerVisibilityPreferences`).
 * Omitting `dancerPreferences` preserves legacy behaviour (registry only).
 */
export function isMediaVisible(
  media: MediaTypeKey,
  ctx: ViewContext,
  dancerPreferences?: DancerVisibilityPreferences,
): boolean {
  const rule = FIELD_VISIBILITY[ctx.surface].media[media];
  if (!rule || !rule(ctx)) return false;
  if (dancerPreferences === undefined) return true;
  if (ctx.viewer === 'admin') return true;
  const prefKey = MEDIA_PREFERENCE_KEY[media];
  if (!prefKey) return true;
  return resolvePreference(dancerPreferences, prefKey);
}

/**
 * Legacy helper — checks visibility on a surface using the default
 * context (`brand: 'flow', viewer: 'anonymous'`). Kept for backward
 * compatibility with call sites that haven't been migrated to
 * `isFieldVisible(field, ctx)`. New code should use `isFieldVisible`.
 */
export function isFieldVisibleInView(
  field: ProfileFieldKey,
  view: ViewKey,
): boolean {
  return isFieldVisible(field, { surface: view, ...DEFAULT_CTX });
}

/**
 * Legacy helper — see `isFieldVisibleInView`.
 */
export function isMediaVisibleInView(
  media: MediaTypeKey,
  view: ViewKey,
): boolean {
  return isMediaVisible(media, { surface: view, ...DEFAULT_CTX });
}

/**
 * Return the field keys visible on a given surface under a given context.
 */
export function getFieldsForViewWithContext(
  ctx: ViewContext,
): ProfileFieldKey[] {
  return (
    Object.keys(FIELD_VISIBILITY[ctx.surface].fields) as ProfileFieldKey[]
  ).filter((field) => isFieldVisible(field, ctx));
}

/**
 * Legacy helper — returns fields visible on the surface under the
 * default context.
 */
export function getFieldsForView(view: ViewKey): ProfileFieldKey[] {
  return getFieldsForViewWithContext({ surface: view, ...DEFAULT_CTX });
}

/**
 * Return the media keys visible on a given surface under a given context.
 */
export function getMediaForViewWithContext(ctx: ViewContext): MediaTypeKey[] {
  return (
    Object.keys(FIELD_VISIBILITY[ctx.surface].media) as MediaTypeKey[]
  ).filter((media) => isMediaVisible(media, ctx));
}

/**
 * Legacy helper — returns media visible on the surface under the
 * default context.
 */
export function getMediaForView(view: ViewKey): MediaTypeKey[] {
  return getMediaForViewWithContext({ surface: view, ...DEFAULT_CTX });
}
